import React, { useState, useEffect } from 'react';
import { UserPlus, Mail } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import CustomSelect from '../common/CustomSelect';
import TextInput from '../common/TextInput';
import LoadingButton from '../common/LoadingButton';
import { FormGroup, InfoBox, WarningBox } from '../common/FormComponents';
import { LoadingState, EmptyState } from '../common/DisplayComponents';
import { Spinner } from '../common/LoadingComponents';
import { authAPI, adminAPI, householdAPI } from '../../services/api';

/**
 * AddMemberModal - Add member to household (with tabs for existing user or invite by email)
 */
function AddMemberModal({ isOpen, onClose, onAdd, onInviteSent }) {
  const [activeTab, setActiveTab] = useState('existing');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  
  // Invite by email form fields
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  
  // SMTP status
  const [checkingSmtp, setCheckingSmtp] = useState(true);
  const [smtpEnabled, setSmtpEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAvailableUsers();
      checkSmtpStatus();
      setSelectedUserId('');
      setSelectedRole('member');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteEmail('');
      setInviteRole('member');
      setError('');
      setActiveTab('existing');
    }
  }, [isOpen]);

  const checkSmtpStatus = async () => {
    setCheckingSmtp(true);
    try {
      const response = await adminAPI.getSettings('smtp');
      const smtp = response.data.setting_value;
      // SMTP is configured if enabled and has host, auth, and from email
      const isConfigured = smtp?.enabled && smtp?.host && smtp?.authUser && smtp?.fromEmail;
      setSmtpEnabled(isConfigured);
    } catch (error) {
      console.error('Failed to check SMTP status:', error);
      setSmtpEnabled(false);
    } finally {
      setCheckingSmtp(false);
    }
  };

  const loadAvailableUsers = async () => {
    setLoading(true);
    try {
      const response = await authAPI.getAvailableUsers();
      setAvailableUsers(response.data);
    } catch (err) {
      setError('Failed to load available users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitExisting = async (e) => {
    e.preventDefault();
    setError('');
    setAdding(true);

    try {
      if (!selectedUserId) {
        setError('Please select a user');
        setAdding(false);
        return;
      }
      await onAdd(selectedUserId, selectedRole);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleSubmitInvite = async (e) => {
    e.preventDefault();
    setError('');
    setAdding(true);

    try {
      // Validation
      if (!inviteEmail || !inviteEmail.includes('@')) {
        setError('Please enter a valid email address');
        setAdding(false);
        return;
      }
      
      if (!inviteFirstName.trim() || !inviteLastName.trim()) {
        setError('Please enter the user\'s first and last name');
        setAdding(false);
        return;
      }

      // Call the invite by email API
      await householdAPI.inviteByEmail({
        email: inviteEmail.trim(),
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        role: inviteRole
      });

      // Trigger success callback from parent
      if (onInviteSent) {
        onInviteSent(inviteEmail);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setAdding(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Member to Household"
      icon={<UserPlus size={24} />}
      size="large"
    >
      {/* Tab Navigation - only show tabs if SMTP is confirmed configured */}
      {smtpEnabled && (
        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'existing' ? 'active' : ''}`}
            onClick={() => setActiveTab('existing')}
            type="button"
          >
            Add Existing User
          </button>
          <button
            className={`modal-tab ${activeTab === 'invite' ? 'active' : ''}`}
            onClick={() => setActiveTab('invite')}
            type="button"
          >
            Invite by Email
          </button>
        </div>
      )}

      {/* Existing User Tab */}
      {activeTab === 'existing' && (
        <form onSubmit={handleSubmitExisting}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            {loading ? (
              <LoadingState message="Loading users..." />
            ) : availableUsers.length === 0 ? (
              <EmptyState 
                title="No users available"
                message="All users are already in a household"
              />
            ) : (
              <>
                <FormGroup label="Select User" htmlFor="user">
                  <CustomSelect
                    name="user"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={adding}
                    placeholder="Choose a user..."
                    options={availableUsers.map(user => ({
                      value: user.id.toString(),
                      label: `${user.first_name} ${user.last_name} (${user.email})`,
                      icon: '👤'
                    }))}
                  />
                </FormGroup>

                <FormGroup label="Assign Role" htmlFor="role" hint="Managers can manage household settings and members">
                  <CustomSelect
                    name="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={adding}
                    options={[
                      { value: 'member', label: 'Member', icon: '👤' },
                      { value: 'manager', label: 'Manager', icon: '👑' }
                    ]}
                  />
                </FormGroup>

                <InfoBox title="ℹ️ About Invitations (Manager)">
                  <p>
                    The selected user will receive a notification with an invitation to join your household.
                    They must log in and accept the invitation before they'll have access to household features.
                  </p>
                </InfoBox>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={adding || loading || availableUsers.length === 0}
            >
              {adding ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      )}

      {/* Invite by Email Tab - only reachable if smtpEnabled */}
      {activeTab === 'invite' && smtpEnabled && (
        <form onSubmit={handleSubmitInvite}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            {checkingSmtp ? (
              <div className="loading-state" style={{ padding: 'var(--spacing-lg)' }}>
                <Spinner size="md" />
                <span style={{ marginLeft: 'var(--spacing-sm)' }}>Checking email configuration...</span>
              </div>
            ) : !smtpEnabled ? (
              <>
                <WarningBox title="Email Not Configured">
                  <p>
                    Email invitations are not available because SMTP is not configured. 
                    Please contact your administrator to enable email functionality, or use the 
                    "Add Existing User" tab to invite users who already have accounts.
                  </p>
                </WarningBox>
              </>
            ) : (
              <>
                <div className="form-row">
                  <FormGroup label="First Name" htmlFor="inviteFirstName" required>
                    <TextInput
                      id="inviteFirstName"
                      name="inviteFirstName"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      placeholder="John"
                      required
                      disabled={adding}
                      autoFocus
                    />
                  </FormGroup>

                  <FormGroup label="Last Name" htmlFor="inviteLastName" required>
                    <TextInput
                      id="inviteLastName"
                      name="inviteLastName"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      placeholder="Doe"
                      required
                      disabled={adding}
                    />
                  </FormGroup>
                </div>

                <FormGroup label="Email Address" htmlFor="inviteEmail" required>
                  <TextInput
                    type="email"
                    id="inviteEmail"
                    name="inviteEmail"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="member@example.com"
                    required
                    disabled={adding}
                  />
                </FormGroup>

                <FormGroup label="Assign Role" htmlFor="inviteRole" hint="Managers can manage household settings and members">
                  <CustomSelect
                    name="inviteRole"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    disabled={adding}
                    options={[
                      { value: 'member', label: 'Member', icon: '👤' },
                      { value: 'manager', label: 'Manager', icon: '👑' }
                    ]}
                  />
                </FormGroup>

                <div className="info-box" style={{ marginTop: 'var(--spacing-md)' }}>
                  <Mail size={18} />
                  <div>
                    <strong>Email Invitation</strong>
                    <p>
                      An email will be sent to this address with a secure link to create their account 
                      and join your household. They will automatically be added to your household once 
                      they accept the invitation.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn-primary"
              loading={adding}
              disabled={checkingSmtp || !smtpEnabled}
            >
              Send Invitation
            </LoadingButton>
          </div>
        </form>
      )}
    </BaseModal>
  );
}

export default AddMemberModal;
